import React, { useEffect, useState } from "react";
import { Button, Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import NoRecord from "../../../assets/SharedComponents/Table/NoRecord";
import { FaFilterIcon } from "../../../assets/Images/icon";
import { BATCH_STATUSES, formatDate, humanize, pillClass } from "../helpers";
import usePagedList from "./usePagedList";
import BatchDetailModal from "./BatchDetailModal";
import { LoadingRow, PerPageSelect, SimplePager } from "./Shared";

/** Pushes across dealerships, newest first; a row opens the per-listing results. */
const PushesTab = ({ subsidiaryId, onSubsidiaryChange }) => {
  const [status, _status] = useState("");
  const [subsidiaryInput, _subsidiaryInput] = useState(subsidiaryId || "");
  const [perPage, _perPage] = useState("");
  const [openBatch, _openBatch] = useState(null);

  useEffect(() => {
    _subsidiaryInput(subsidiaryId || "");
  }, [subsidiaryId]);

  const list = usePagedList("/batches", {
    "filter[status]": status,
    "filter[subsidiary_id]": subsidiaryId,
    per_page: perPage,
  });

  // An empty box clears the filter; anything else must be a dealership id. Falling back to
  // "" on bad input showed EVERY dealership to someone who asked for one — the opposite of
  // what they typed, with nothing on screen to say so.
  const applySubsidiary = () => {
    const value = String(subsidiaryInput).trim();

    if (value === "") {
      onSubsidiaryChange("");

      return;
    }

    if (!/^\d+$/.test(value)) {
      toast.error("Dealership must be a numeric id.", { position: toast.POSITION.TOP_RIGHT });

      return;
    }

    onSubsidiaryChange(value);
  };

  const clearFilter = () => {
    _status("");
    _perPage("");
    onSubsidiaryChange("");
  };

  return (
    <>
      <div className="filters">
        <select className="filter-select" value={status} onChange={(e) => _status(e.target.value)}>
          <option value="">All statuses</option>
          {BATCH_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.name}
            </option>
          ))}
        </select>
        <input
          className="filter-input"
          placeholder="Dealership id"
          inputMode="numeric"
          value={subsidiaryInput}
          onChange={(e) => _subsidiaryInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applySubsidiary()}
        />
        <button className="btn btn-secondary" onClick={applySubsidiary}>
          Filter
        </button>
        <PerPageSelect value={perPage} onChange={_perPage} />
        <Tooltip title="Clear Filter" placement="bottom">
          <FaFilterIcon className="filter-icon" role="button" onClick={clearFilter} />
        </Tooltip>
      </div>

      <div className="tableContainer">
        <table className="auctionTable">
          <thead className="tableHead">
            <tr className="table-row">
              <th className="time-start">Push</th>
              <th className="time-start">Dealership</th>
              <th className="time-start">Status</th>
              <th className="time-start">Listings</th>
              <th className="time-start">Created / updated / unchanged</th>
              <th className="time-start">Failed</th>
              <th className="time-start">Completed</th>
              <th className="time-start">Details</th>
            </tr>
          </thead>
          {list.loading ? (
            <LoadingRow colSpan={8} />
          ) : (
            <tbody>
              {list.rows.length > 0 ? (
                list.rows.map((batch) => (
                  <tr key={`batch-${batch.id}`} className="table-row">
                    <td>
                      <b>#{batch.id}</b>
                      <div className="muted">{formatDate(batch.accepted_at)}</div>
                    </td>
                    <td>
                      {batch.subsidiary?.name ?? "-"}
                      <div className="muted">#{batch.subsidiary_id}</div>
                    </td>
                    <td>
                      <span className={pillClass(batch.status)}>{humanize(batch.status)}</span>
                    </td>
                    <td>
                      {batch.item_count}
                      {batch.skipped_count > 0 && <div className="muted">+{batch.skipped_count} skipped</div>}
                    </td>
                    <td>
                      {batch.created_count} / {batch.updated_count} / {batch.unchanged_count}
                    </td>
                    <td className={batch.failed_count > 0 ? "text-danger fw-bold" : ""}>{batch.failed_count}</td>
                    <td>{formatDate(batch.completed_at)}</td>
                    <td>
                      <Button variant="outlined" onClick={() => _openBatch(batch.id)} sx={{ textTransform: "none" }}>
                        View
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <NoRecord />
              )}
            </tbody>
          )}
        </table>
      </div>
      <SimplePager page={list.page} hasPrev={list.hasPrev} hasNext={list.hasNext} onChange={list.setPage} />

      <BatchDetailModal batchId={openBatch} onClose={() => _openBatch(null)} />
    </>
  );
};

export default PushesTab;
